/**
 * Player Core logs to an internal mini framework which emits nothing by
 * default.
 *
 * The log framework supports the standard log levels (debug, log, info, warn,
 * error), and is used as follows:
 *
 * ```ts
 * import { getLogger } from '@nrk/player-core';
 *
 * const logger = getLogger('myModule');
 *
 * logger.log('Hello');
 * ```
 *
 * The log calls are handled by a {@link LogHandler} implementation. For
 * example, to use the `ConsoleLogHandler`, do:
 *
 * ```ts
 * import { addLogHandler, ConsoleLogHandler } from '@nrk/player-core';
 *
 * addLogHandler(new ConsoleLogHandler());
 * ```
 */

/**
 */
let LogLevel;

(function (LogLevel) {
  LogLevel["Debug"] = "debug";
  LogLevel["Log"] = "log";
  LogLevel["Info"] = "info";
  LogLevel["Warn"] = "warn";
  LogLevel["Error"] = "error";
})(LogLevel || (LogLevel = {}));

const logHandlers = [];
function addLogHandler(handler) {
  logHandlers.push(handler);
}
function getLogger(namespace) {
  // The proxy is bound, but the current log handler isn't and can be changed
  // at any time.
  function proxy(namespace, level, msg, ...msgs) {
    logHandlers.forEach(handler => handler.log(namespace, level, msg, ...msgs));
  }

  return {
    [LogLevel.Debug]: proxy.bind(proxy, namespace, LogLevel.Debug),
    [LogLevel.Log]: proxy.bind(proxy, namespace, LogLevel.Log),
    [LogLevel.Info]: proxy.bind(proxy, namespace, LogLevel.Info),
    [LogLevel.Warn]: proxy.bind(proxy, namespace, LogLevel.Warn),
    [LogLevel.Error]: proxy.bind(proxy, namespace, LogLevel.Error),
    metric: proxy.bind(proxy, 'Metric', LogLevel.Log)
  };
}
async function logMetric(logger, name, action) {
  const start = Date.now();

  try {
    await action();
    const end = Date.now();
    logger.metric(name, end - start);
  } catch (e) {
    const end = Date.now();
    logger.metric(`${name}-error`, end - start);
    throw e;
  }
}

export { LogLevel as L, addLogHandler as a, getLogger as g, logMetric as l };
